import { loadLocaleCatalog } from '@kilat/config-seed';
import type { Locale } from '@kilat/shared';

/**
 * The locale catalogues this session has actually loaded.
 *
 * Marketing used to import `defaultI18n`, which statically pulls all four
 * locale files — 1.25 MB — into the entry chunk. Every visitor downloaded
 * every language and every namespace, including the 1,788-key `dashboard.*`
 * tree that this site never renders. Measured 2026-08-22: the entry was 1.5 MB
 * and first contentful paint sat at 8-12s on simulated mobile against a 1.5s
 * budget, because the JS starved the render-blocking stylesheet of bandwidth.
 *
 * Now a visitor downloads the language they are reading, plus English as the
 * fallback. `main.tsx` awaits both BEFORE mounting React, which is safe here
 * because the app uses `createRoot` rather than `hydrateRoot`: the pre-rendered
 * HTML stays on screen untouched until React replaces it, so waiting costs no
 * visible time and cannot produce a flash of the wrong language.
 */
const catalogs = new Map<Locale, Record<string, unknown>>();

/** Catalogue for a locale, or undefined if it has not been loaded yet. */
export function getCatalog(locale: Locale): Record<string, unknown> | undefined {
  return catalogs.get(locale);
}

/**
 * Load these locales if they are not already in memory.
 *
 * Idempotent and safe to call repeatedly — a locale switch mid-session calls it
 * for the newly chosen language.
 */
export async function preloadCatalogs(locales: readonly Locale[]): Promise<void> {
  await Promise.all(
    locales.map(async (locale) => {
      if (catalogs.has(locale)) return;
      catalogs.set(locale, await loadLocaleCatalog(locale));
    }),
  );
}
